/**
 * Pure helpers extracting MeshPreview inputs from a line config
 * snapshot. Shared between read-only previews (cart row, project
 * detail) so we keep one source of truth for "what does this part look
 * like?". Snapshot keys (``color_code`` / ``material_code``) are
 * catalog/data-driven — no process-specific logic. The part's orientation is
 * not read here: it is an interactive input (``rotationOfInputs``, KAN-1703).
 */
import type { Catalog } from "@/api/types";

type Snapshot = Record<string, unknown> | null | undefined;

/**
 * Resolve mesh colour hex from a line snapshot: customer-picked colour
 * (``color_code``) → material's default ``swatch_hex`` → undefined
 * (MeshPreview falls back to its violet default).
 *
 * The picked colour resolves first from the selected material's
 * ``option_sources`` (the consolidated per-material option lists that drive the
 * configurator "Barva" picker — the single source of truth), then the legacy
 * ``catalog.color_options`` table for pre-consolidation catalogs. Keeps read-only
 * previews (cart row, project detail) in step with the live configurator.
 */
export function resolveColorOptionHex(
  catalog: Catalog | undefined,
  snapshot: Snapshot,
): string | undefined {
  if (!catalog) return undefined;
  const colorCode =
    typeof snapshot?.color_code === "string" ? snapshot.color_code : null;
  const materialCode =
    typeof snapshot?.material_code === "string" ? snapshot.material_code : null;
  const material = materialCode
    ? catalog.materials.find((m) => m.code === materialCode)
    : undefined;
  if (colorCode) {
    // Consolidated path: the chosen option's hex from the selected material's
    // option lists. Only colour options carry a hex, so scanning every list is
    // safe (image-swatch / plain option lists have none → skipped).
    for (const options of Object.values(material?.option_sources ?? {})) {
      const option = options.find((o) => o.code === colorCode);
      if (option?.hex) return option.hex;
    }
    // Legacy color_options table (pre-consolidation catalogs).
    const color = (catalog.color_options ?? []).find((c) => c.code === colorCode);
    if (color?.swatch_hex) return color.swatch_hex;
  }
  if (material?.swatch_hex) return material.swatch_hex;
  return undefined;
}
